import { db } from '../firebase';
import { collection, query, where, getDocs } from '../firestore-compat';

/** Converte "HH:mm" para minutos desde 00:00 */
export function timeToMinutes(timeStr) {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

/** Converte minutos para "HH:mm" */
export function minutesToTime(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Checa se dois intervalos se sobrepõem: [s1, e1) e [s2, e2) */
function isOverlap(s1, e1, s2, e2) {
    return s1 < e2 && e1 > s2;
}

// Horário comercial
const BUSINESS_START = 8 * 60;  // 08:00 → 480 min
const BUSINESS_END   = 18 * 60; // 18:00 → 1080 min

const collectionsToCheck = [
    { name: 'agenda_pessoal',       participantField: 'participantesUids', nameField: 'participantes' },
    { name: 'auditorias',           participantField: 'participantesUids', nameField: 'auditores' },
    { name: 'auditorias_homecare',  participantField: 'participantesUids', nameField: 'auditores' },
    { name: 'agendaDirecao',        participantField: 'participantesUids', nameField: 'responsaveis' }
];

/** Busca todos os eventos agendados de um dia nas 4 coleções. */
async function fetchAllEventsForDate(date) {
    const allEvents = [];
    for (const coll of collectionsToCheck) {
        try {
            const q = query(
                collection(db, coll.name),
                where('data', '==', date),
                where('status', '==', 'Agendado')
            );
            const snapshot = await getDocs(q);
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const startMins = timeToMinutes(data.horario || '08:00');
                const endMins   = data.horarioFim ? timeToMinutes(data.horarioFim) : startMins + 60;
                allEvents.push({
                    id: doc.id,
                    collection: coll.name,
                    data,
                    startMins,
                    endMins,
                    uids:  data.participantesUids || [],
                    names: data[coll.nameField]   || []
                });
            });
        } catch (err) {
            console.error(`[conflictUtils] Erro ao buscar ${coll.name}:`, err);
        }
    }
    return allEvents;
}

/**
 * Retorna os UIDs de todos os participantes ocupados no intervalo especificado.
 */
export async function getBusyUids(date, startTime, endTime, excludeEventId = null) {
    if (!date || !startTime || !endTime) return [];
    const targetStart = timeToMinutes(startTime);
    const targetEnd   = timeToMinutes(endTime);
    const allEvents   = await fetchAllEventsForDate(date);
    const busyUids    = new Set();

    for (const ev of allEvents) {
        if (excludeEventId && ev.id === excludeEventId) continue;
        if (isOverlap(targetStart, targetEnd, ev.startMins, ev.endMins)) {
            if (ev.data.userId) busyUids.add(ev.data.userId);
            ev.uids.forEach(uid => busyUids.add(uid));
        }
    }
    return Array.from(busyUids);
}

/**
 * Checa se UM usuário específico tem conflito no intervalo. (Retrocompatibilidade)
 */
export async function checkScheduleConflict(userUid, userName, date, startTime, endTime = null, excludeEventId = null) {
    if (!userUid || !date || !startTime) return { hasConflict: false };
    const targetStart = timeToMinutes(startTime);
    const targetEnd   = endTime ? timeToMinutes(endTime) : targetStart + 1;
    const allEvents   = await fetchAllEventsForDate(date);

    for (const ev of allEvents) {
        if (excludeEventId && ev.id === excludeEventId) continue;
        if (isOverlap(targetStart, targetEnd, ev.startMins, ev.endMins)) {
            if (ev.uids.includes(userUid) || ev.data.userId === userUid) {
                return { hasConflict: true, conflictEvent: { ...ev.data, id: ev.id, collection: ev.collection } };
            }
            if (userName && ev.names.includes(userName)) {
                return { hasConflict: true, conflictEvent: { ...ev.data, id: ev.id, collection: ev.collection } };
            }
        }
    }
    return { hasConflict: false };
}

/**
 * Encontra o próximo horário livre em horário comercial (08:00–18:00)
 * onde TODOS os participantes estão disponíveis.
 */
export async function findFreeTime(date, durationMinutes, participantsUids, excludeEventId = null) {
    if (!date || !durationMinutes || !participantsUids?.length) return null;
    const allEvents     = await fetchAllEventsForDate(date);
    const relevantEvents = allEvents.filter(ev => {
        if (excludeEventId && ev.id === excludeEventId) return false;
        return ev.uids.some(uid => participantsUids.includes(uid)) ||
               (ev.data.userId && participantsUids.includes(ev.data.userId));
    });

    const stepMins = 30;

    for (let start = BUSINESS_START; start + durationMinutes <= BUSINESS_END; start += stepMins) {
        const end = start + durationMinutes;
        const hasConflict = relevantEvents.some(ev => isOverlap(start, end, ev.startMins, ev.endMins));
        if (!hasConflict) return minutesToTime(start);
    }
    return null; // Não encontrou nenhum slot
}

/**
 * NOVA FUNÇÃO — Retorna detalhes de disponibilidade por participante.
 *
 * Retorna um array de objetos:
 * {
 *   uid: string,
 *   name: string,
 *   disponivel: boolean,
 *   conflitos: [{ descricao, horario, horarioFim, collection }],
 *   horariosLivres: [{ inicio: "HH:mm", fim: "HH:mm" }]   ← slots de 1h durante o dia
 * }
 */
export async function getParticipantsAvailability(participantsUids, date, startTime, endTime, systemUsers, excludeEventId = null) {
    if (!participantsUids?.length || !date) return [];

    const targetStart = timeToMinutes(startTime || '08:00');
    const targetEnd   = timeToMinutes(endTime   || '09:00');
    const allEvents   = await fetchAllEventsForDate(date);

    return participantsUids.map(uid => {
        const user = systemUsers.find(u => u.id === uid);
        const name = user?.name || 'Usuário';

        // Todos os eventos desse participante neste dia
        const myEvents = allEvents.filter(ev => {
            if (excludeEventId && ev.id === excludeEventId) return false;
            return ev.uids.includes(uid) || ev.data.userId === uid;
        });

        // Verifica conflito no horário solicitado
        const conflitos = myEvents
            .filter(ev => isOverlap(targetStart, targetEnd, ev.startMins, ev.endMins))
            .map(ev => ({
                descricao:  ev.data.descricao || ev.data.auditoria || 'Compromisso',
                horario:    ev.data.horario    || minutesToTime(ev.startMins),
                horarioFim: ev.data.horarioFim || minutesToTime(ev.endMins),
                collection: ev.collection
            }));

        // Calcula horários livres entre 08:00 e 18:00 (slots de 1h)
        const horariosLivres = [];
        const slotDuration   = 60; // 1 hora
        for (let s = BUSINESS_START; s + slotDuration <= BUSINESS_END; s += 30) {
            const e = s + slotDuration;
            const busy = myEvents.some(ev => isOverlap(s, e, ev.startMins, ev.endMins));
            if (!busy) {
                // Evitar duplicatas de slots muito próximos — agrupar em blocos maiores
                const last = horariosLivres[horariosLivres.length - 1];
                if (last && timeToMinutes(last.fim) === s) {
                    last.fim = minutesToTime(e); // Estende o bloco livre
                } else {
                    horariosLivres.push({ inicio: minutesToTime(s), fim: minutesToTime(e) });
                }
            }
        }

        return {
            uid,
            name,
            disponivel: conflitos.length === 0,
            conflitos,
            horariosLivres
        };
    });
}
